import { useTranslation } from 'react-i18next';
import { Link, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { AlertTriangle, Lock, Clock } from '@/components/icons';
import { EmptyState } from '@/components/ui/empty-state';
import { PageContainer } from '@/components/layout/PageContainer';

/**
 * Canonical route-level error boundary per
 * `sustevo-handbook/brand/ERROR_PAGES.md` § 2.
 *
 * Mounted as `errorElement` on the root router so any thrown route
 * action / loader surfaces here. Classifies into the four canonical
 * variants (`unauthenticated` / `forbidden` / `rate-limited` /
 * `unknown-server`) and renders a distinct CTA for each.
 *
 * Stays inside the AppShell — the chrome is intact, the user can
 * still navigate. Only the route segment failed.
 */

type Classification =
  | { kind: 'unauthenticated' }
  | { kind: 'forbidden' }
  | { kind: 'rate-limited' }
  | { kind: 'unknown-server'; digest?: string };

function classify(error: unknown): Classification {
  if (isRouteErrorResponse(error)) {
    if (error.status === 401) return { kind: 'unauthenticated' };
    if (error.status === 403) return { kind: 'forbidden' };
    if (error.status === 429) return { kind: 'rate-limited' };
  }
  // Heuristic: ApiError-style classification for non-RouteErrorResponse
  // throws from loaders / actions.
  const e = error as { status?: number; digest?: string };
  if (e?.status === 401) return { kind: 'unauthenticated' };
  if (e?.status === 403) return { kind: 'forbidden' };
  if (e?.status === 429) return { kind: 'rate-limited' };
  return { kind: 'unknown-server', digest: e?.digest };
}

export function RouteErrorPage(): JSX.Element {
  const { t } = useTranslation();
  const error = useRouteError();
  const classification = classify(error);

  // Report unknown-server to Sentry — these are real failures we want
  // visibility on. Auth + rate-limit errors are expected operational
  // states; no Sentry noise for those.
  if (classification.kind === 'unknown-server') {
    if (typeof window !== 'undefined' && 'Sentry' in window) {
      try {
        // Lazy-access avoids a hard import of @sentry/react in tests.
        const Sentry = (window as { Sentry?: { captureException?: (e: unknown) => void } }).Sentry;
        Sentry?.captureException?.(error);
      } catch {
        // never block error rendering on a telemetry failure
      }
    }
  }

  let icon: JSX.Element;
  let title: string;
  let body: string;
  let action: JSX.Element;

  switch (classification.kind) {
    case 'unauthenticated':
      icon = <Lock className="h-10 w-10" aria-hidden="true" />;
      title = t('errors.route.unauthenticated.title', {
        defaultValue: 'Your session expired',
      });
      body = t('errors.route.unauthenticated.body', {
        defaultValue: 'Sign in again to continue where you left off.',
      });
      action = (
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('errors.route.unauthenticated.signIn', { defaultValue: 'Sign in again' })}
        </Link>
      );
      break;
    case 'forbidden':
      icon = <Lock className="h-10 w-10" aria-hidden="true" />;
      title = t('errors.route.forbidden.title', {
        defaultValue: "You don't have access",
      });
      body = t('errors.route.forbidden.body', {
        defaultValue:
          'This area requires a permission your account does not have. Contact your administrator if you believe this is a mistake.',
      });
      action = (
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground hover:bg-muted"
        >
          {t('errors.route.forbidden.backHome', { defaultValue: 'Back to home' })}
        </Link>
      );
      break;
    case 'rate-limited':
      icon = <Clock className="h-10 w-10" aria-hidden="true" />;
      title = t('errors.route.rateLimited.title', {
        defaultValue: 'Slow down a bit',
      });
      body = t('errors.route.rateLimited.body', {
        defaultValue:
          'Too many requests in a short window. Wait a few seconds, then try again.',
      });
      action = (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground hover:bg-muted"
        >
          {t('errors.route.rateLimited.retry', { defaultValue: 'Try again' })}
        </button>
      );
      break;
    case 'unknown-server':
    default:
      icon = <AlertTriangle className="h-10 w-10" aria-hidden="true" />;
      title = t('errors.route.unknownServer.title', {
        defaultValue: 'Something went wrong',
      });
      body = t('errors.route.unknownServer.body', {
        defaultValue:
          'We hit an unexpected problem. The team has been notified. Try again or come back in a minute.',
      });
      action = (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t('errors.route.unknownServer.retry', { defaultValue: 'Try again' })}
        </button>
      );
      break;
  }

  return (
    <div
      data-testid="route-error-page"
      data-kind={classification.kind}
    >
      {/* #1701 — was `mx-auto max-w-[1200px] px-10 py-12` which
          double-padded against the AppShell's already-padded `<main>`
          (`px-4 py-6 lg:px-8`). PageContainer with `paddingX="none"`
          lets the shell own the horizontal gutter. */}
      <PageContainer size="wide" paddingX="none" paddingY="loose">
        <EmptyState
          icon={icon}
          title={title}
          description={body}
          action={action}
        />
        {classification.kind === 'unknown-server' && classification.digest ? (
          <p className="mt-4 text-center font-mono text-[11px] text-muted-foreground">
            ref: {classification.digest}
          </p>
        ) : null}
      </PageContainer>
    </div>
  );
}
